import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, PhoneCall, PhoneOff, Headphones, Play, Shuffle, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  getRoleplayHome, startRoleplay, finishRoleplay, abandonRoleplay, getRoleplay, rerateRoleplay, getRecordingAudio,
  getRoleplayAudio, getRoleplayTeam, getDiscovery,
} from "@/lib/api";

// Role play against an AI (Gemini Live), in two modes: a full in-gym consult with an AI member
// (Script Practice), or a Lead Nurture phone call to an AI lead who enquired from an ad (Lead Nurture board). The browser streams the
// rep's mic to Gemini (16 kHz PCM) and plays the member's voice (24 kHz PCM) using a single-use key
// from FPA. Both sides are transcribed live; at the end FPA rates the call like a real consult.

const DIFF = { easy: "Easy", realistic: "Realistic", tough: "Tough" };
const OUTCOME = { consult_booked: "Consult booked", callback_agreed: "Callback agreed", not_interested: "Not interested",
  no_real_conversation: "No real conversation", other: "Other" };
const COPY = {
  consult: { eyebrow: "Live practice", title: "Role play a full consult", who: "Who walks in", start: "Start the consult",
    body: "An AI member sits down for a consult and you run it from Part 1 to the close. They answer like a real walk-in, only open up when you dig, and push back at the price. When you end the call, FPA rates it against the official script.",
    random: "Like a real walk-in: you don't know who's coming", rating: "Rating your consult against the official script. This takes a minute or two." },
  nurture: { eyebrow: "Call practice", title: "Practise a lead call", who: "Who you're calling", start: "Call the lead",
    body: "You call an AI lead who filled in an ad form. They pick up like a real lead: busy, half-remembering the form, asking the price. Your job is the same as on a real call: book the goal-setting consult with a day and time. FPA rates it against the Lead Nurture script.",
    random: "Like a fresh lead: you don't know who'll pick up", rating: "Rating your call against the Lead Nurture script. This takes a minute or two." },
};
const DEPTH = { not_asked: "Didn't ask why", surface: "Surface", one_level_deeper: "One level deeper", real_pain: "Real pain" };
const err = (e, fallback) => toast.error(e?.response?.data?.detail || fallback);
const clock = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

// Mic frames → main thread. Kept inline so there's no extra file to serve.
const WORKLET = `class Cap extends AudioWorkletProcessor { process(inputs) { const ch = inputs[0] && inputs[0][0];
  if (ch) this.port.postMessage(ch.slice(0)); return true; } } registerProcessor('cap', Cap);`;

function toBase64(int16) {
  const bytes = new Uint8Array(int16.buffer);
  let bin = "";
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  return btoa(bin);
}
function fromBase64Pcm(b64) {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const pcm = new Int16Array(bytes.buffer, 0, Math.floor(bytes.length / 2));
  const out = new Float32Array(pcm.length);
  for (let i = 0; i < pcm.length; i++) out[i] = pcm[i] / 32768;
  return out;
}
function downsampleTo16k(frame, rate) {
  if (rate === 16000) return frame;
  const ratio = rate / 16000;
  const out = new Float32Array(Math.floor(frame.length / ratio));
  for (let i = 0; i < out.length; i++) out[i] = frame[Math.floor(i * ratio)];
  return out;
}
const pickMime = () => ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"].find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || "";

function Rating({ play }) {
  const [url, setUrl] = useState(null);
  const r = play.rating;
  if (!r) return null;
  const loadAudio = () => (play.mode === "nurture" ? getRoleplayAudio(play.id) : getRecordingAudio(play.recording_id));
  return (
    <div className="space-y-4" data-testid="roleplay-rating">
      <div className="flex flex-wrap items-end gap-3">
        <div className="font-display text-5xl font-bold tabular-nums">{r.overall_score}<span className="text-xl text-zinc-500">/100</span></div>
        {r.outcome && <span className={`px-3 py-1 rounded-full border text-sm ${r.outcome === "consult_booked" ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-amber-500/30 bg-amber-500/10 text-amber-300"}`}>
          {OUTCOME[r.outcome] || r.outcome}{r.booked_for ? `: ${r.booked_for}` : ""}</span>}
        {r.discovery_depth && <span className="px-3 py-1 rounded-full border border-blue-500/30 bg-blue-500/10 text-blue-300 text-sm">Discovery: {DEPTH[r.discovery_depth]}</span>}
      </div>
      {r.summary && <p className="text-zinc-300">{r.summary}</p>}
      {r.tone_note && <p className="text-sm text-zinc-400">Tone: {r.tone_note}</p>}
      {!!r.part_scores?.length && (
        <div className="grid gap-2 sm:grid-cols-2">
          {r.part_scores.map((p) => (
            <div key={p.part}>
              <div className="flex justify-between text-sm"><span className="text-zinc-300">{p.part}</span><span className="tabular-nums text-zinc-400">{p.score}</span></div>
              <div className="h-2 rounded-full bg-zinc-800 mt-1 overflow-hidden">
                <div className={`h-full rounded-full ${p.score >= 80 ? "bg-emerald-500" : p.score >= 50 ? "bg-amber-500" : "bg-rose-500"}`} style={{ width: `${p.score}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
      {r.coaching_feedback && <div><div className="eyebrow mb-1">What to work on</div><p className="text-sm text-zinc-200 whitespace-pre-line">{r.coaching_feedback}</p></div>}
      <div className="grid gap-3 sm:grid-cols-2 text-sm">
        {!!r.strengths?.length && <div><div className="eyebrow mb-1">Went well</div><ul className="list-disc pl-5 text-zinc-300 space-y-1">{r.strengths.map((s, i) => <li key={i}>{s}</li>)}</ul></div>}
        {!!r.missed_points?.length && <div><div className="eyebrow mb-1">Missed</div><ul className="list-disc pl-5 text-zinc-300 space-y-1">{r.missed_points.map((s, i) => <li key={i}>{s}</li>)}</ul></div>}
      </div>
      {!!r.objections?.length && (
        <div><div className="eyebrow mb-1">Objections</div>
          <ul className="text-sm space-y-1">{r.objections.map((o, i) => <li key={i}><span className="text-zinc-200">"{o.objection}"</span> <span className="text-zinc-500">· {o.handling}</span></li>)}</ul></div>
      )}
      {r.discovery_depth_note && <p className="text-xs text-zinc-500">{r.discovery_depth_note}</p>}
      {play.has_audio && (url ? <audio src={url} controls className="w-full" /> : (
        <Button variant="outline" size="sm" className="rounded-full" onClick={() => loadAudio().then((b) => setUrl(URL.createObjectURL(b))).catch((e) => err(e, "Couldn't load the audio"))}>
          <Play className="h-4 w-4 mr-1" />Listen back</Button>))}
      {play.transcript && <details className="text-sm"><summary className="cursor-pointer text-zinc-400">Transcript</summary>
        <p className="mt-2 text-zinc-300 whitespace-pre-line leading-relaxed">{play.transcript}</p></details>}
    </div>
  );
}

function Call({ start, onEnded }) {
  const [phase, setPhase] = useState("connecting");     // connecting | live | ending
  const [secs, setSecs] = useState(0);
  const [talking, setTalking] = useState(false);
  const [captions, setCaptions] = useState([]);
  const r = useRef({});

  const pushText = useCallback((speaker, text) => {
    const st = r.current;
    const t = (Date.now() - st.t0) / 1000;
    const segs = st.segs;
    const last = segs[segs.length - 1];
    if (last && last.speaker === speaker) { last.text += text; last.end = t; }
    else segs.push({ speaker, start: t, end: t, text });
    setCaptions(segs.slice(-4).map((s) => ({ ...s })));
  }, []);

  const teardown = useCallback(() => {
    const st = r.current;
    clearInterval(st.timer);
    try { st.ws?.close(); } catch { /* closed */ }
    st.mic?.getTracks().forEach((t) => t.stop());
    (st.playing || []).forEach((s) => { try { s.stop(); } catch { /* done */ } });
    try { st.inCtx?.close(); } catch { /* closed */ }
    try { st.wake?.release(); } catch { /* ignore */ }
  }, []);

  const end = useCallback(async (reason) => {
    const st = r.current;
    if (st.ended) return;
    st.ended = true;
    setPhase("ending");
    const seconds = Math.round((Date.now() - (st.t0 || Date.now())) / 1000);
    const blob = await new Promise((res) => {
      if (!st.rec || st.rec.state === "inactive") return res(null);
      st.rec.onstop = () => res(new Blob(st.chunks, { type: st.mime }));
      st.rec.stop();
    });
    teardown();
    try { st.outCtx?.close(); } catch { /* closed */ }
    if (reason === "cancel" || !st.t0) {
      abandonRoleplay(start.id, seconds).catch(() => {});
      onEnded(null);
      return;
    }
    try {
      const file = blob && blob.size ? new File([blob], `roleplay.${st.mime.includes("mp4") ? "m4a" : "webm"}`, { type: st.mime }) : null;
      const play = await finishRoleplay(start.id, seconds, st.segs, file);
      onEnded(play);
    } catch (e) {
      err(e, "Couldn't save the call");
      abandonRoleplay(start.id, seconds).catch(() => {});
      onEnded(null);
    }
  }, [start.id, teardown, onEnded]);

  useEffect(() => {
    const st = r.current;
    st.segs = [];
    st.chunks = [];
    st.playing = [];
    let cancelled = false;
    (async () => {
      try {
        st.mic = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
      } catch { toast.error("FPA needs microphone access for the role play."); abandonRoleplay(start.id, 0).catch(() => {}); onEnded(null); return; }
      if (cancelled) { st.mic.getTracks().forEach((t) => t.stop()); return; }
      try { st.wake = await navigator.wakeLock?.request("screen"); } catch { /* not supported */ }
      // Playback (24 kHz) plus a mix of both voices for the saved recording.
      st.outCtx = start.outCtx;
      st.outCtx.resume?.();
      st.mixDest = st.outCtx.createMediaStreamDestination();
      st.outCtx.createMediaStreamSource(st.mic).connect(st.mixDest);
      st.mime = pickMime();
      st.rec = new MediaRecorder(st.mixDest.stream, st.mime ? { mimeType: st.mime } : {});
      st.mime = st.rec.mimeType || st.mime || "audio/webm";
      st.rec.ondataavailable = (e) => { if (e.data?.size) st.chunks.push(e.data); };
      st.nextAt = 0;

      const ws = new WebSocket(`${start.ws_url}?access_token=${encodeURIComponent(start.token)}`);
      st.ws = ws;
      ws.onopen = () => ws.send(JSON.stringify({ setup: start.setup }));
      ws.onerror = () => { if (!st.t0) { toast.error("Couldn't connect to the AI member. Try again."); end("cancel"); } };
      ws.onclose = (ev) => { if (!st.ended) { if (st.t0) end("closed"); else { toast.error(`The call couldn't start (${ev.reason || ev.code}).`); end("cancel"); } } };
      ws.onmessage = async (ev) => {
        const msg = JSON.parse(typeof ev.data === "string" ? ev.data : await ev.data.text());
        if (msg.setupComplete) { await beginStreaming(); return; }
        const sc = msg.serverContent;
        if (msg.goAway) toast.message("The call is about to end.");
        if (!sc) return;
        if (sc.interrupted) {
          st.playing.forEach((s) => { try { s.stop(); } catch { /* done */ } });
          st.playing = [];
          st.nextAt = 0;
          setTalking(false);
        }
        for (const part of sc.modelTurn?.parts || []) {
          if (part.inlineData?.data) playChunk(part.inlineData.data);
        }
        if (sc.inputTranscription?.text) pushText("coach", sc.inputTranscription.text);
        if (sc.outputTranscription?.text) pushText("member", sc.outputTranscription.text);
      };

      const playChunk = (b64) => {
        const ctx = st.outCtx;
        const data = fromBase64Pcm(b64);
        if (!data.length) return;
        const buf = ctx.createBuffer(1, data.length, 24000);
        buf.copyToChannel(data, 0);
        const src = ctx.createBufferSource();
        src.buffer = buf;
        src.connect(ctx.destination);
        src.connect(st.mixDest);
        const at = Math.max(ctx.currentTime + 0.03, st.nextAt);
        src.start(at);
        st.nextAt = at + buf.duration;
        st.playing.push(src);
        setTalking(true);
        src.onended = () => {
          st.playing = st.playing.filter((s) => s !== src);
          if (!st.playing.length) setTalking(false);
        };
      };

      const beginStreaming = async () => {
        const ctx = start.inCtx;
        st.inCtx = ctx;
        await ctx.resume?.();
        await ctx.audioWorklet.addModule(URL.createObjectURL(new Blob([WORKLET], { type: "application/javascript" })));
        const node = new AudioWorkletNode(ctx, "cap");
        let pending = [];
        let count = 0;
        node.port.onmessage = (e) => {
          const f = downsampleTo16k(e.data, ctx.sampleRate);
          pending.push(f);
          count += f.length;
          if (count < 1600 || ws.readyState !== 1) return;          // ~100 ms per message
          const pcm = new Int16Array(count);
          let o = 0;
          for (const p of pending) for (let i = 0; i < p.length; i++) pcm[o++] = Math.max(-1, Math.min(1, p[i])) * 32767;
          pending = []; count = 0;
          ws.send(JSON.stringify({ realtimeInput: { audio: { data: toBase64(pcm), mimeType: "audio/pcm;rate=16000" } } }));
        };
        ctx.createMediaStreamSource(st.mic).connect(node);
        st.rec.start(1000);
        st.t0 = Date.now();
        setPhase("live");
        st.timer = setInterval(() => {
          const s = (Date.now() - st.t0) / 1000;
          setSecs(s);
          if (s >= start.max_seconds) { toast.message("Time's up for this call."); end("time"); }
        }, 500);
        // The member speaks first, as someone who has just sat down.
        ws.send(JSON.stringify({ clientContent: { turns: [{ role: "user", parts: [{ text: start.opening || "(The coach has just welcomed you in. Greet them.)" }] }], turnComplete: true } }));
      };
    })();
    return () => { cancelled = true; if (!st.ended) { st.ended = true; teardown(); try { st.outCtx?.close(); } catch { /* closed */ } } };
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const left = start.max_seconds - secs;
  return (
    <section className="card-surface p-5 sm:p-7 space-y-5" data-testid="roleplay-call">
      <div className="flex items-center gap-4">
        <div className={`h-16 w-16 rounded-full grid place-items-center text-2xl font-bold shrink-0 transition ${talking ? "bg-emerald-500/20 text-emerald-300 ring-4 ring-emerald-500/30" : "bg-zinc-800 text-zinc-300"}`}>
          {start.persona.name[0]}
        </div>
        <div className="min-w-0">
          <div className="font-display text-xl font-bold">{start.persona.name}</div>
          <div className="text-sm text-zinc-400">{start.persona.blurb}</div>
          <div className="text-sm mt-1">
            {phase === "connecting" && <span className="flex items-center gap-2 text-zinc-400"><Loader2 className="h-4 w-4 animate-spin" />Connecting…</span>}
            {phase === "live" && <span className={left < 60 ? "text-amber-300" : "text-zinc-300"}>
              <span className="tabular-nums">{clock(secs)}</span> · {talking ? `${start.persona.name} is talking` : "Listening"}{left < 60 ? ` · ${Math.max(0, Math.round(left))}s left` : ""}</span>}
            {phase === "ending" && <span className="flex items-center gap-2 text-zinc-400"><Loader2 className="h-4 w-4 animate-spin" />Saving the call…</span>}
          </div>
        </div>
      </div>
      <div className="space-y-2 min-h-[120px]" aria-live="polite">
        {captions.map((c, i) => (
          <p key={i} className={`text-base ${c.speaker === "member" ? "text-zinc-100" : "text-zinc-500"}`}>
            <span className="text-xs uppercase tracking-wide mr-2">{c.speaker === "member" ? start.persona.name : "You"}</span>{c.text.trim()}</p>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => end("done")} disabled={phase !== "live"} className="rounded-full bg-rose-600 hover:bg-rose-500 px-6" data-testid="roleplay-end">
          <PhoneOff className="h-4 w-4 mr-2" />End and get my score</Button>
        <Button variant="ghost" onClick={() => end("cancel")} disabled={phase === "ending"} className="rounded-full text-zinc-400">Cancel</Button>
      </div>
    </section>
  );
}

function NurtureTeam({ onOpen }) {
  const [team, setTeam] = useState(null);
  useEffect(() => { getRoleplayTeam().then(setTeam).catch(() => setTeam({ rows: [] })); }, []);
  if (!team) return null;
  return (
    <section className="card-surface p-5 space-y-3">
      <div className="eyebrow">Team practice calls</div>
      {!team.rows.length ? <p className="text-sm text-zinc-500">Nobody has practised a lead call yet.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead><tr className="text-left text-xs uppercase tracking-wide text-zinc-500">
              <th className="py-2 pr-3">Rep</th><th className="py-2 pr-3">Calls</th><th className="py-2 pr-3">Booked</th><th className="py-2 pr-3">Avg</th><th className="py-2 pr-3">Best</th><th className="py-2">Latest</th></tr></thead>
            <tbody>{team.rows.map((r) => (
              <tr key={r.user_id} className="border-t border-[#1f1f1f] align-top">
                <td className="py-2 pr-3 font-medium">{r.name}</td><td className="py-2 pr-3 tabular-nums">{r.count}</td>
                <td className="py-2 pr-3 tabular-nums">{r.booked}</td><td className="py-2 pr-3 tabular-nums">{r.avg ?? "–"}</td>
                <td className="py-2 pr-3 tabular-nums">{r.best ?? "–"}</td>
                <td className="py-2">{r.recent.map((p) => (
                  <button key={p.id} type="button" onClick={() => onOpen(p.id)} className="block text-left text-zinc-300 hover:text-white">
                    {p.persona_name} ({p.difficulty}) · {p.score ?? "–"}{p.outcome === "consult_booked" ? " · booked" : ""}</button>))}</td>
              </tr>))}</tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// Levels 4 and 5 of a script part: a short role play of that part only, with the member dropped in at that point.
export function PartRolePlay({ part, level, passMark = 80, track = "consult", onExit }) {
  const [starting, setStarting] = useState(false);
  const [call, setCall] = useState(null);
  const [brief, setBrief] = useState(null);
  const [play, setPlay] = useState(null);
  const poll = useCallback((id) => {
    let tries = 0;
    const tick = () => getRoleplay(id).then((p) => {
      setPlay(p);
      if (!(p.rating_status === "done" || p.rating_status === "error" || ++tries > 60)) setTimeout(tick, 3000);
    }).catch(() => setTimeout(tick, 5000));
    tick();
  }, []);
  const begin = async () => {
    if (!navigator.mediaDevices?.getUserMedia || !window.AudioWorkletNode || !window.WebSocket) return toast.error("This browser can't run the role play. Update your phone's browser.");
    setStarting(true);
    const outCtx = new AudioContext({ sampleRate: 24000 });
    let inCtx;
    try { inCtx = new AudioContext({ sampleRate: 16000 }); } catch { inCtx = new AudioContext(); }
    try {
      const st = await startRoleplay("random", level === 5 ? "tough" : "realistic", "part", { part: part.n, level, track });
      setBrief(st);
      setCall({ ...st, outCtx, inCtx });
    } catch (e) { err(e, "Couldn't start the role play"); outCtx.close(); inCtx.close(); }
    setStarting(false);
  };
  const passed = play?.rating && play.rating.overall_score >= passMark;
  return (
    <div className="space-y-4" data-testid="part-roleplay">
      <button type="button" onClick={() => onExit()} className="text-sm text-zinc-400 hover:text-zinc-200">← All parts</button>
      <section className="card-surface p-5 sm:p-6 space-y-3">
        <div className="eyebrow">Part {part.n} · Level {level}: {level === 5 ? "hard role play" : "role play"}</div>
        <h2 className="font-display text-xl font-bold">{part.title}</h2>
        {!call && !play && (
          <>
            <p className="text-sm text-zinc-400 max-w-2xl">{track === "nurture"
              ? "An AI lead is on the phone, at this point in the call. Run only this part, then end the call to get your score."
              : "An AI member joins the consult at this point. Run only this part, then end the call to get your score."}
              {level === 5 ? (track === "nurture" ? " This lead is tough: wary, short on time and pushing on price." : " This member is tough: guarded, sceptical and quick to push back.") : ""}</p>
            <p className="text-sm text-zinc-300 flex items-center gap-2"><Headphones className="h-4 w-4" />Use earphones, in a quiet corner.</p>
            <Button onClick={begin} disabled={starting} className="rounded-full bg-emerald-600 hover:bg-emerald-500 px-6" data-testid="part-roleplay-start">
              {starting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PhoneCall className="h-4 w-4 mr-2" />}Start the role play</Button>
          </>
        )}
        {brief && !play && (
          <div className="rounded-lg border border-[#2a2a2a] p-3 text-sm space-y-1">
            <div className="text-zinc-200">{brief.brief}</div>
            {brief.story && <ul className="text-zinc-400 list-disc pl-5">{brief.story.map((l, i) => <li key={i}>{l}</li>)}</ul>}
          </div>
        )}
      </section>
      {call && <Call start={call} onEnded={(p) => { setCall(null); if (p) { setPlay(p); poll(p.id); } else { setBrief(null); } }} />}
      {play && (
        <section className="card-surface p-5 sm:p-6 space-y-4">
          {play.rating ? (<>
            <span className={`inline-block px-3 py-1 rounded-full border text-sm font-semibold ${passed ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-amber-500/15 text-amber-300 border-amber-500/30"}`}>
              {passed ? "Passed" : "Try again"}</span>
            <Rating play={play} />
          </>) : play.error ? (
            <div className="space-y-2"><p className="text-rose-300">{play.error}</p>
              <Button onClick={() => rerateRoleplay(play.id).then(() => poll(play.id))}>Try rating again</Button></div>
          ) : <p className="flex items-center gap-2 text-zinc-300"><Loader2 className="h-4 w-4 animate-spin" />Rating this part. This takes about a minute.</p>}
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => { setPlay(null); setBrief(null); }} className="rounded-full"><RotateCcw className="h-4 w-4 mr-1" />Try again</Button>
            {passed && level === 4 && <Button onClick={() => onExit(5)} className="rounded-full bg-emerald-600 hover:bg-emerald-500">Level 5: hard role play</Button>}
            <Button variant="outline" onClick={() => onExit()} className="rounded-full">All parts</Button>
          </div>
        </section>
      )}
    </div>
  );
}

export default function RolePlay({ mode = "consult" }) {
  const copy = COPY[mode] || COPY.consult;
  const [home, setHome] = useState(null);
  const [persona, setPersona] = useState("random");
  const [difficulty, setDifficulty] = useState("realistic");
  const [starting, setStarting] = useState(false);
  const [call, setCall] = useState(null);
  const [play, setPlay] = useState(null);
  const load = useCallback(() => getRoleplayHome(mode).then(setHome).catch((e) => err(e, "Couldn't load role play")), [mode]);
  useEffect(() => { load(); }, [load]);

  const poll = useCallback((id) => {
    let tries = 0;
    const tick = () => getRoleplay(id).then((p) => {
      setPlay(p);
      if (p.rating_status === "done" || p.rating_status === "error" || ++tries > 60) load();
      else setTimeout(tick, 3000);
    }).catch(() => setTimeout(tick, 5000));
    tick();
  }, [load]);

  if (!home) return <Loader2 className="h-4 w-4 animate-spin text-zinc-500" />;
  if (call) return <Call start={call} onEnded={(p) => { setCall(null); if (p) { setPlay(p); poll(p.id); } else load(); }} />;

  if (play) {
    return (
      <section className="card-surface p-5 sm:p-7 space-y-4">
        <div className="eyebrow">{mode === "nurture" ? "Practice call" : "Role play"} · {play.user_name ? `${play.user_name} · ` : ""}{play.persona_name} · {DIFF[play.difficulty]}</div>
        {play.rating ? <Rating play={play} /> : play.error ? (
          <div className="space-y-2"><p className="text-rose-300">{play.error}</p>
            <Button onClick={() => rerateRoleplay(play.id).then(() => poll(play.id))}>Try rating again</Button></div>
        ) : <p className="flex items-center gap-2 text-zinc-300"><Loader2 className="h-4 w-4 animate-spin" />{copy.rating}</p>}
        <Button variant="outline" className="rounded-full" onClick={() => setPlay(null)}><RotateCcw className="h-4 w-4 mr-1" />Back</Button>
      </section>
    );
  }

  const left = Math.max(0, Math.round(home.daily_minutes - home.used_today));
  const begin = async () => {
    if (!navigator.mediaDevices?.getUserMedia || !window.AudioWorkletNode || !window.WebSocket) return toast.error("This browser can't run the role play. Update your phone's browser.");
    setStarting(true);
    // Audio contexts are made here, on the tap: phones only let audio start from a user gesture.
    const outCtx = new AudioContext({ sampleRate: 24000 });
    let inCtx;
    try { inCtx = new AudioContext({ sampleRate: 16000 }); } catch { inCtx = new AudioContext(); }
    try { setCall({ ...(await startRoleplay(persona, difficulty, mode)), outCtx, inCtx }); } catch (e) {
      err(e, "Couldn't start the call");
      outCtx.close(); inCtx.close();
    }
    setStarting(false);
  };
  return (
    <div className="space-y-4">
      <section className="card-surface p-5 sm:p-6 space-y-4">
        <div>
          <div className="eyebrow">{copy.eyebrow}</div>
          <h3 className="font-display text-lg font-bold mt-1">{copy.title}</h3>
          <p className="text-sm text-zinc-400 mt-1 max-w-2xl">{copy.body}</p>
          <p className="text-sm text-zinc-300 mt-2 flex items-center gap-2"><Headphones className="h-4 w-4" />Use earphones, in a quiet corner. Up to {home.max_minutes} minutes a call; {left} minutes left today.</p>
        </div>
        {home.full_unlocked === false && (
          <p className="text-sm text-amber-300">Opens once all {home.full_unlock_parts} parts have passed level 4 (the role play of that part). You've finished {home.parts_done} of {home.full_unlock_parts}. Work through the parts first.</p>
        )}
        {!home.ready && <p className="text-sm text-amber-300">{home.pilot_open === false
          ? "Role play is being piloted at another gym first. It's coming to your gym soon."
          : "Role play isn't switched on yet. It needs the Gemini key on the server."}</p>}
        <div>
          <div className="eyebrow mb-2">{copy.who}</div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            <button type="button" onClick={() => setPersona("random")}
              className={`rounded-lg border p-3 text-left ${persona === "random" ? "border-white bg-white/5" : "border-[#2a2a2a] hover:border-zinc-500"}`}>
              <div className="font-semibold flex items-center gap-2"><Shuffle className="h-4 w-4" />Surprise me</div>
              <div className="text-xs text-zinc-500">{copy.random}</div>
            </button>
            {home.personas.map((p) => (
              <button key={p.id} type="button" onClick={() => setPersona(p.id)}
                className={`rounded-lg border p-3 text-left ${persona === p.id ? "border-white bg-white/5" : "border-[#2a2a2a] hover:border-zinc-500"}`}>
                <div className="font-semibold">{p.name}, {p.age}</div><div className="text-xs text-zinc-500">{p.blurb}</div>
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="eyebrow mb-2">How hard</div>
          <div className="flex gap-2">{home.difficulties.map((d) => (
            <button key={d} type="button" onClick={() => setDifficulty(d)}
              className={`px-4 py-2 rounded-full text-sm ${difficulty === d ? "bg-white text-black font-semibold" : "border border-[#2a2a2a] text-zinc-300"}`}>{DIFF[d]}</button>))}</div>
        </div>
        <Button onClick={begin} disabled={!home.ready || starting || left < 1 || home.full_unlocked === false} className="rounded-full bg-emerald-600 hover:bg-emerald-500 px-6" data-testid="roleplay-start">
          {starting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PhoneCall className="h-4 w-4 mr-2" />}{copy.start}</Button>
      </section>
      {!!home.recent.length && (
        <div className="card-surface divide-y divide-[#1f1f1f]">
          {home.recent.filter((p) => p.recording_id).map((p) => (
            <button key={p.id} type="button" className="w-full p-3 flex flex-wrap justify-between gap-2 text-sm text-left hover:bg-white/[0.02]"
              onClick={() => getRoleplay(p.id).then(setPlay).catch((e) => err(e, "Couldn't open it"))}>
              <span>{p.persona_name} · {DIFF[p.difficulty]} · {Math.round((p.seconds || 0) / 60)} min</span>
              <span className="tabular-nums text-zinc-400">{new Date(p.started_at).toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })} · {p.rating ? `${p.rating.overall_score}/100` : p.rating_status === "error" ? "not rated" : "rating…"}</span>
            </button>
          ))}
        </div>
      )}
      {mode === "nurture" && home.can_see_nurture_team && <NurtureTeam onOpen={(id) => getRoleplay(id).then(setPlay).catch((e) => err(e, "Couldn't open it"))} />}
    </div>
  );
}

// Today's training (27 Oct): a 30-minute discovery intensive. First the AI is the coach and the rep plays a member
// from a card, so they hear what deep discovery sounds like. Then they swap: the AI is a member with a hidden real
// reason, and the rep digs. Every call is up to 7 minutes and ends with pointers.
const DEMO_TARGET = 2;
const DRILL_TARGET = 3;
const NOTICE = ["One question at a time, then silence while you answer",
  "Every follow-up uses your own words: \"Get fitter. Why is that important to you?\"",
  "At least three levels deep, until there's a feeling, a person or an event",
  "No pitch, no price, no programme talk during discovery",
  "A short summary in your words at the end: \"So you told me...\""];

function CardBox({ card, demo }) {
  return (
    <div className="rounded-lg border border-blue-500/30 bg-blue-500/5 p-4 text-sm space-y-1" data-testid="discovery-card">
      <div className="eyebrow">{demo ? "You are this member" : "The member's real reason was"}</div>
      {demo ? (<>
        <p><span className="text-zinc-400">Say first:</span> <span className="text-white">"{card.goal}"</span></p>
        <p><span className="text-zinc-400">If they ask a follow-up:</span> <span className="text-white">"{card.practical}"</span></p>
        <p><span className="text-zinc-400">Only after they keep digging:</span> <span className="text-white">"{card.real}"</span></p>
        <p className="text-xs text-zinc-500 pt-1">Keep your answers short. Don't give the next layer unless the coach earns it.</p>
      </>) : <p className="text-white">"{card.real}"</p>}
    </div>
  );
}

export function DiscoveryTraining({ passMark = 80 }) {
  const [home, setHome] = useState(null);
  const [call, setCall] = useState(null);
  const [play, setPlay] = useState(null);
  const [card, setCard] = useState(null);
  const [starting, setStarting] = useState(false);
  const load = useCallback(() => getDiscovery().then(setHome).catch((e) => err(e, "Couldn't load today's training")), []);
  useEffect(() => { load(); }, [load]);
  const poll = useCallback((id) => {
    let tries = 0;
    const tick = () => getRoleplay(id).then((p) => {
      setPlay(p);
      if (p.rating_status === "done" || p.rating_status === "error" || ++tries > 60) load();
      else setTimeout(tick, 3000);
    }).catch(() => setTimeout(tick, 5000));
    tick();
  }, [load]);
  if (!home) return <Loader2 className="h-4 w-4 animate-spin text-zinc-500" />;
  const done = (m) => home.today.filter((x) => x.mode === m && (x.status === "rated" || x.status === "rating")).length;
  const demos = done("demo"), drills = done("drill");
  const day = home.day || { title: "Discovery intensive: go deeper with your whys", demos: DEMO_TARGET, drills: DRILL_TARGET, intro: "" };
  const nextMode = demos < day.demos ? "demo" : "drill";
  const dayLabel = day.date ? new Date(`${day.date}T12:00:00`).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" }) : "";
  const left = Math.max(0, Math.floor(home.daily_minutes - home.used_minutes));
  const pickCard = () => {
    const seen = new Set(home.today.map((x) => x.drill_card));
    const fresh = home.cards.filter((c) => !seen.has(c.id) && c.id !== card?.id);
    return (fresh.length ? fresh : home.cards)[Math.floor(Math.random() * (fresh.length || home.cards.length))];
  };
  const begin = async (mode) => {
    if (!navigator.mediaDevices?.getUserMedia || !window.AudioWorkletNode || !window.WebSocket) return toast.error("This browser can't run the role play. Update your phone's browser.");
    setStarting(true);
    setPlay(null);
    const c = pickCard();
    setCard({ ...c, mode });
    const outCtx = new AudioContext({ sampleRate: 24000 });
    let inCtx;
    try { inCtx = new AudioContext({ sampleRate: 16000 }); } catch { inCtx = new AudioContext(); }
    try {
      const st = await startRoleplay("random", "realistic", mode, { card: c.id });
      setCall({ ...st, outCtx, inCtx });
    } catch (e) { err(e, "Couldn't start the call"); outCtx.close(); inCtx.close(); setCard(null); }
    setStarting(false);
  };
  if (call) {
    return (
      <div className="space-y-4">
        {call.mode === "demo" && <CardBox card={call.card} demo />}
        {call.mode === "drill" && <p className="text-sm text-zinc-300">{call.brief}</p>}
        <Call start={call} onEnded={(p) => { setCall(null); if (p) { setPlay(p); if (p.mode === "drill") poll(p.id); else load(); } else load(); }} />
      </div>
    );
  }
  const Step = ({ n, title, body, count, target }) => (
    <div className={`rounded-lg border p-4 ${count >= target ? "border-emerald-500/40 bg-emerald-500/5" : "border-[#262626]"}`}>
      <div className="flex items-center justify-between gap-2"><span className="eyebrow">Step {n}</span>
        <span className={`text-xs tabular-nums ${count >= target ? "text-emerald-300" : "text-zinc-400"}`}>{count}/{target}</span></div>
      <div className="font-semibold mt-1">{title}</div><p className="text-sm text-zinc-400 mt-1">{body}</p>
    </div>
  );
  return (
    <div className="space-y-4" data-testid="discovery-training">
      <section className="card-surface p-5 sm:p-6 space-y-4">
        <div>
          <div className="eyebrow">{home.is_today === false ? `Next AI practice · ${dayLabel}` : `Today's training · ${dayLabel} · 30 minutes`}</div>
          <h2 className="font-display text-xl sm:text-2xl font-bold mt-1">{day.title}</h2>
          <p className="text-sm text-zinc-400 mt-1 max-w-2xl">{day.intro} Each call is up to {home.minutes} minutes, and you get pointers
            after every one. Use earphones, in a quiet corner.{home.is_today === false ? " You can practise on it now too." : ""}</p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Step n={1} title="Watch it done: the AI is the coach" count={demos} target={day.demos}
            body="You play a member from a card. Notice how the coach keeps asking why, using your words, until you share the real reason." />
          <Step n={2} title="Your turn: the AI is the member" count={drills} target={day.drills}
            body={day.style === "summary" ? "Dig to the real reason, ask what they've tried and when they last felt their best, then sum it up in their words. They only agree if you got the real reason."
              : day.style === "guarded" ? "This member answers in one line. Keep following up on their words, four or five times if you need to. No pitch, no price."
              : "The member gives you a goal and hides the real reason. Dig with at least three whys. No pitch, no price. Then summarise."} />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={() => begin(nextMode)} disabled={starting || !home.ready || left < 1} className="rounded-full bg-emerald-600 hover:bg-emerald-500 px-6" data-testid="discovery-start">
            {starting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PhoneCall className="h-4 w-4 mr-2" />}
            {nextMode === "demo" ? `Watch call ${demos + 1}: AI coach` : `Your call ${drills + 1}: AI member`}</Button>
          <Button variant="outline" onClick={() => begin(nextMode === "demo" ? "drill" : "demo")} disabled={starting || !home.ready || left < 1} className="rounded-full border-[#262626]">
            {nextMode === "demo" ? "Skip to my turn" : "Watch the AI coach again"}</Button>
          <span className="text-xs text-zinc-500">{left} of {home.daily_minutes} minutes left today</span>
        </div>
        {!home.ready && <p className="text-sm text-amber-300">The AI calls aren't switched on on the server yet.</p>}
      </section>

      {play && (
        <section className="card-surface p-5 sm:p-6 space-y-4" data-testid="discovery-result">
          {play.mode === "demo" ? (<>
            <div className="eyebrow">Pointers from that call</div>
            <h3 className="font-display text-lg font-bold">What the coach did</h3>
            <ul className="list-disc pl-5 text-sm text-zinc-300 space-y-1">{NOTICE.map((t) => <li key={t}>{t}</li>)}</ul>
            {!!play.rating?.questions?.length && (<div>
              <div className="eyebrow mb-1">The questions the coach asked you</div>
              <ol className="list-decimal pl-5 text-sm text-zinc-200 space-y-1">{play.rating.questions.map((q, i) => <li key={i}>{q}</li>)}</ol>
            </div>)}
            <p className="text-sm text-zinc-400">Borrow these. Next, it's your turn to ask them.</p>
          </>) : play.rating ? (<>
            <span className={`inline-block px-3 py-1 rounded-full border text-sm font-semibold ${play.rating.overall_score >= passMark ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-amber-500/15 text-amber-300 border-amber-500/30"}`}>
              {play.rating.overall_score >= passMark ? "You found it" : "Go deeper next time"}</span>
            <Rating play={play} />
            {card && card.mode === "drill" && <CardBox card={card} />}
          </>) : play.error ? <p className="text-rose-300">{play.error}</p>
            : <p className="flex items-center gap-2 text-zinc-300"><Loader2 className="h-4 w-4 animate-spin" />Getting your pointers. This takes about a minute.</p>}
          <Button onClick={() => begin(nextMode)} disabled={starting || left < 1} className="rounded-full"><RotateCcw className="h-4 w-4 mr-1" />Next call</Button>
        </section>
      )}
    </div>
  );
}
